import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle, Calendar, Settings, AlertTriangle, X, Pencil, BookOpen, Map as MapIcon, Shield, BarChart3, Activity, ToggleLeft, ToggleRight, BellOff, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { getPlayers, getScenarios, createScenario, updateScenario, deleteScenario, getGPS, saveGPS, deleteGPS, getLoadScenarios, createLoadScenario, updateLoadScenario, deleteLoadScenario, getOpponents, saveDrillBatch, getPlayerAliases, createPlayerAlias, uploadGkSessions, getGkSessions, deleteGkSession, uploadTechnicalStatsKnvb, getTechnicalStats, deleteTechnicalStats, uploadCmjTests, getTests, deleteTest, resolveTargetsBatch, getRawTargets, uploadTrackingImport, getTrackingMatches, deleteTrackingMatch } from '../api';
import ScenarioTemplateEditor, { DayMetricsTable, emptyDayTargets, POSITIONS, METRICS } from './ScenarioTemplateEditor';
import ScenarioMapModal, { SESSION_LABELS } from './ScenarioMapModal';
import DocumentUploadSection from './documents/DocumentUploadSection';
import { SCENARIOS } from '../scenarioDays';

// Groups flat gk_sessions rows (one per player per session) into session
// cards keyed by date+title, matching the shape the GK sessions list renders.
function groupGkSessions(rows) {
  const byKey = {};
  (rows || []).forEach(r => {
    const date = r.session_date ? r.session_date.slice(0, 10) : '';
    const key = `${date}__${r.session_title}`;
    if (!byKey[key]) byKey[key] = { key, date, title: r.session_title, players: [] };
    byKey[key].players.push(r);
  });
  return Object.values(byKey).sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Groups flat test_cmj rows (one per player per test date) into date cards,
// same shape/pattern as groupGkSessions above — test_cmj has no session concept
// of its own (it's the same table the manual Physical Test Input CMJ form writes
// to), so the test date is the closest equivalent to a "session" for display.
function groupCmjTests(rows) {
  const byDate = {};
  (rows || []).forEach(r => {
    const date = r.test_date ? r.test_date.slice(0, 10) : '';
    if (!byDate[date]) byDate[date] = { key: date, date, players: [] };
    byDate[date].players.push(r);
  });
  return Object.values(byDate).sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Shared "latest 3, expand for the rest" list shape for the Upload Zone's imported-files tiles
// (2026-09-11) — these lists only ever grow, and were getting long enough to push the page's
// other cards far down. Every caller already passes its items pre-sorted newest-first, so
// slicing here just trims the tail rather than re-sorting.
function CollapsibleList({ items, renderItem, visibleCount = 3 }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, visibleCount);
  return (
    <>
      {shown.map(renderItem)}
      {items.length > visibleCount && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '4px auto 0', padding: '6px 8px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}
        >
          {expanded ? <>Show less <ChevronUp size={14} /></> : <>Show all {items.length} <ChevronDown size={14} /></>}
        </button>
      )}
    </>
  );
}

// Local-time date add (no UTC round-trip) — matches the convention already used
// in EWMA.js / scenarioDays.js for calendar-day arithmetic.
function addDaysLocal(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const parts = String(timeStr).trim().split(':');
  if (parts.length === 3) {
    const [h, m, s] = parts.map(Number);
    return Math.round(h * 60 + m + s / 60);
  }
  if (parts.length === 2) {
    const [m, s] = parts.map(Number);
    return Math.round(m + s / 60);
  }
  return null;
};

// Metrics Table page (2026-09-03) — metrics never parsed before this whose exact Statsports
// header text wasn't confirmed up front (Entries Zone 5/6 turned out to need this treatment too:
// the first guess, 'Entries_zone5'/'Entries_zone6', was wrong and silently produced zeros until
// a real export's header row showed the actual names — now hardcoded directly, like every other
// proven column below, once confirmed). Each remaining entry here lists candidate header names
// in order of likeliness; resolveMetricHeaders picks whichever one is actually present in this
// CSV's header row, so a near-miss still gets picked up instead of always requiring the first
// guess to be exactly right — and anything that matches none of them surfaces as a warning
// banner instead of silently storing zeros (see the unmatchedMetricWarning state below).
const NEW_METRIC_HEADER_ALIASES = {
  avgMetabolicPower: { label: 'Average Metabolic Power', tried: ['Average Metabolic Power', 'Metabolic Power (Average)'] },
  hrv: { label: 'Heart Rate Variability', tried: ['Heart Rate Variability', 'HRV'] },
  modTrimp: { label: 'modTrimp', tried: ['modTRIMP', 'Modified TRIMP', 'modTrimp', 'mTRIMP'] },
};

// Resolves each NEW_METRIC_HEADER_ALIASES entry against this upload's actual header row.
// Returns { headerMap: { avgMetabolicPower: 'Average Metabolic Power' | null, ... }, unmatched: [{label, tried}] }
// so the caller can both read the right column AND warn about any guess that didn't pan out.
function resolveMetricHeaders(headers) {
  const headerMap = {};
  const unmatched = [];
  Object.entries(NEW_METRIC_HEADER_ALIASES).forEach(([key, { label, tried }]) => {
    const found = tried.find(h => headers.includes(h));
    headerMap[key] = found || null;
    if (!found) unmatched.push({ label, tried });
  });
  return { headerMap, unmatched };
}

// Builds one player's "load" metrics (the numbers that land in gps_data) from their
// "Entire Session" summary row(s) for the day. Usually there's exactly one, and this is
// a straight passthrough. When two real sessions share the same Session Date + Session
// Title (2026-07-30 — confirmed case: two MD-2 sessions on one day), there are two, and
// their load needs adding together rather than picking one and dropping the other.
// Additive quantities (distances, counts, minutes, sprints) are summed; peak values (max
// speed, max HR) take the max across sessions, not a sum, since a max isn't cumulative;
// average HR is averaged across sessions. HR-zone time breakdowns and dist/min are
// display-only, lower-stakes fields — HR zones come from the first row rather than
// merging time strings, and dist/min is recomputed from the summed TD/minutes so it stays
// a true rate instead of an average-of-rates.
function buildMetricsFromRows(sourceRows, headerMap = {}) {
  const sum = key => sourceRows.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);
  const max = key => Math.max(...sourceRows.map(r => parseFloat(r[key]) || 0));
  const avgOfRows = key => sourceRows.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0) / sourceRows.length || 0;
  const first = sourceRows[0];
  const totalMinutes = sourceRows.reduce((s, r) => s + (parseTimeToMinutes(r['Total Time']) || 0), 0) || null;
  const totalTd = sum('Total Distance');
  return {
    TD: totalTd,
    RD: sum('Distance Zone 4 - Zone 6 (Absolute)'),
    HID: sum('High Speed Running (Absolute)'),
    SD: sum('Distance Zone 6 (Absolute)'),
    SA: sourceRows.reduce((s, r) => s + (parseInt(r['Accelerations (Absolute)']) || 0) + (parseInt(r['Decelerations (Absolute)']) || 0), 0),
    Expl: sum('Explosive Distance (Absolute)'),
    HMLD: sum('HML Distance'),
    maxSpeed: max('Max Speed'),
    playerMaxSpeed: max('Player Max Speed'),
    minutesPlayed: totalMinutes,
    avgHR: Math.round(sourceRows.reduce((s, r) => s + (parseFloat(r['Average Heart Rate']) || 0), 0) / sourceRows.length) || 0,
    maxHR: Math.round(max('Max Heart Rate')) || 0,
    distPerMin: totalMinutes ? totalTd / totalMinutes : (parseFloat(first['Distance Per Min']) || 0),
    sprints: sum('Sprints'),
    // Metrics Table page's "High Intensity Runs >19,8km/h" (zone5+zone6) and "Sprints >25,2km/h"
    // (zone6 alone) columns — confirmed against a real export's header row 2026-09-03 (the
    // initial guess, 'Entries_zone5'/'Entries_zone6', was wrong; the real headers are these).
    entriesZone5: sum('Entries Zone 5 (Relative)'),
    entriesZone6: sum('Entries Zone 6 (Absolute)'),
    avgMetabolicPower: headerMap.avgMetabolicPower ? avgOfRows(headerMap.avgMetabolicPower) : 0,
    hrv: headerMap.hrv ? avgOfRows(headerMap.hrv) : 0,
    modTrimp: headerMap.modTrimp ? sum(headerMap.modTrimp) : 0,
    hrZ1: first['Time In Heart Rate Zone 1 (Relative)'] || '',
    hrZ2: first['Time In Heart Rate Zone 2 (Relative)'] || '',
    hrZ3: first['Time In Heart Rate Zone 3 (Relative)'] || '',
    hrZ4: first['Time In Heart Rate Zone 4 (Relative)'] || '',
    hrZ5: first['Time In Heart Rate Zone 5 (Relative)'] || '',
    hrZ6: first['Time In Heart Rate Zone 6 (Relative)'] || '',
  };
}

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };


const emptyScenario = { scenario: '1B', startDate: '', playerId: 'team', intensity: 100, mdMinutes: 90, targetOverrides: {} };

// Minimal inline resolver for an unmatched Technical Stats CSV name — pick the player, save an
// alias (reuses the existing player_name_aliases table/API, same one Settings' "Player Name
// Aliases" panel manages), so this exact CSV name resolves automatically on every future
// import instead of being re-flagged every time.
function UnmatchedNameResolver({ name, players, onResolved }) {
  const [playerId, setPlayerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!playerId) return;
    setSaving(true);
    setError('');
    try {
      await createPlayerAlias({ player_id: playerId, alias_name: name });
      onResolved(name);
    } catch (err) {
      console.error('Failed to save alias:', err);
      setError('Failed to save — that name may already be aliased.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0' }}>
      <span style={{ fontSize: 13, color: '#fca5a5', minWidth: 180 }}>{name}</span>
      <span style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>is…</span>
      <select value={playerId} onChange={e => setPlayerId(e.target.value)}
        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit' }}>
        <option value="">Pick a player…</option>
        {players.map(p => <option key={p.id} value={p.id}>{p.name}{p.active === false ? ' (inactive)' : ''}</option>)}
      </select>
      <button disabled={!playerId || saving} onClick={handleSave}
        style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: playerId ? '#3b82f6' : 'var(--border)', color: playerId ? '#fff' : 'var(--text-dimmed)', cursor: playerId ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
        {saving ? 'Saving…' : 'Save alias'}
      </button>
      {error && <span style={{ fontSize: 11, color: '#fca5a5' }}>{error}</span>}
    </div>
  );
}

export default function GPSUpload() {
  const [players, setPlayers]         = useState([]);
  const [scenarios, setScenarios]     = useState([]);
  const [sessions, setSessions]       = useState([]);
  const [gkSessions, setGkSessions]   = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  // Built-in TARGETS/SCENARIO_DAYS, fetched from /api/targets/raw (targetResolution.js, backend)
  // instead of this file's own hand-copied TSV — see the 2026-09-01 individualized-targets pass.
  const [builtInTables, setBuiltInTables] = useState({ targets: {}, scenarioDays: {} });
  const [uploadStatus, setUploadStatus] = useState(null);
  // Metrics Table page (2026-09-03) — set after a CSV parse when one of the still-guessed new
  // metric headers (avg metabolic power / HRV / modTrimp) wasn't found in this file's header
  // row, so a wrong guess is visible on the very first real upload instead of silently storing
  // zeros forever. Cleared at the start of every new upload.
  const [unmatchedMetricWarning, setUnmatchedMetricWarning] = useState(null);
  // Defaults to on (today's existing behavior) — switched off when backfilling old matches so
  // players/staff don't get paged for every historical game being imported.
  const [sendNotifications, setSendNotifications] = useState(true);
  const [gkUploadStatus, setGkUploadStatus] = useState(null);
  const [gkUploadResult, setGkUploadResult] = useState(null);
  // KNVB Technical Stats import (2026-08-17) — the only Technical Stats source now (SciSports's
  // own techUploadStatus/Result/Error + resolvedAliasNames state lived here before the club
  // fully switched sources; removed along with its upload card). Names resolved via the inline
  // alias form below are tracked locally so the "unmatched" list can show them as resolved
  // without needing to re-upload the same CSV just to refresh it.
  const [techKnvbUploadStatus, setTechKnvbUploadStatus] = useState(null);
  const [techKnvbUploadResult, setTechKnvbUploadResult] = useState(null);
  const [techKnvbUploadError, setTechKnvbUploadError] = useState('');
  const [resolvedAliasNamesKnvb, setResolvedAliasNamesKnvb] = useState(new Set());
  const [technicalStatsSessions, setTechnicalStatsSessions] = useState([]);
  // KNVB tracking (fysiek) import — Team Comparison feature (2026-09-11). trackingPendingUpload
  // holds the parsed rows+filename between the initial POST's 409 and the user confirming
  // overwrite, since re-parsing the file a second time would mean re-prompting for it.
  const [trackingUploadStatus, setTrackingUploadStatus] = useState(null);
  const [trackingUploadResult, setTrackingUploadResult] = useState(null);
  const [trackingUploadError, setTrackingUploadError] = useState('');
  const [trackingExisting, setTrackingExisting] = useState(null);
  const [trackingPendingUpload, setTrackingPendingUpload] = useState(null);
  const [trackingMatches, setTrackingMatches] = useState([]);
  const [cmjTests, setCmjTests] = useState([]);
  const [cmjUploadStatus, setCmjUploadStatus] = useState(null);
  const [cmjUploadResult, setCmjUploadResult] = useState(null);
  // Same per-import tracking as resolvedAliasNames above, just for the CMJ card's own
  // unmatched list (independent Set so resolving a name in one import doesn't affect the other).
  const [resolvedCmjAliasNames, setResolvedCmjAliasNames] = useState(new Set());
  const [showForm, setShowForm]       = useState(false);
  // Club Planner (2026-10-02): after saving a team scenario, offer to fill the planner's week
  // schedule from that scenario's template — { scenario, weeks: ['2026-W41', ...] }.
  const [plannerOffer, setPlannerOffer] = useState(null);
  const [showEditPicker, setShowEditPicker] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [form, setForm]               = useState(emptyScenario);
  const [templateEditor, setTemplateEditor] = useState(null); // null | 'new' | templateObj
  const [showScenarioMap, setShowScenarioMap] = useState(false);
  const [showAllScenarios, setShowAllScenarios] = useState(false);
  const [opponents, setOpponents] = useState([]);
  const [expandedOverrideDates, setExpandedOverrideDates] = useState(() => new Set());
  const [aliases, setAliases] = useState([]);

  // Resolve a CSV player name to the canonical player record — direct match
  // first, then falls back to a known alias (e.g. Statsports export typos).
  const resolvePlayer = (csvName) => {
    if (!csvName) return null;
    const lower = csvName.toLowerCase();
    // Active-only, matching the backend's own resolvePlayerId — an inactive
    // duplicate (e.g. a renamed player whose old record was retired) must not
    // win over an alias that deliberately redirects this CSV name elsewhere.
    const direct = players.find(p => p.name.toLowerCase() === lower && p.active);
    if (direct) return direct;
    const alias = aliases.find(a => a.alias_name.toLowerCase() === lower);
    if (alias) return players.find(p => String(p.id) === String(alias.player_id)) || null;
    // Last resort: an inactive player with no alias configured (e.g. genuinely
    // departed) still resolves by exact name rather than silently dropping data.
    return players.find(p => p.name.toLowerCase() === lower) || null;
  };

  // Merged lookup maps (built-in, fetched via /api/targets/raw, + custom templates kept fresh
  // client-side through this page's own create/update/delete handlers below)
  const allTargets = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.targets; });
    return { ...builtInTables.targets, ...custom };
  }, [customTemplates, builtInTables]);

  const allScenarioDays = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.days; });
    return { ...builtInTables.scenarioDays, ...custom };
  }, [customTemplates, builtInTables]);

  // Merged label lookup (built-in SESSION_LABELS + each custom template's own stored labels) —
  // so copying a day from your own scenario carries over ITS note/color, not a blank one.
  const allLabels = useMemo(() => {
    const custom = {};
    customTemplates.forEach(t => { custom[t.name] = t.labels || {}; });
    return { ...SESSION_LABELS, ...custom };
  }, [customTemplates]);

  const allScenarioNames = useMemo(() => {
    const customNames = customTemplates.map(t => t.name);
    const extra = customNames.filter(n => !SCENARIOS.includes(n));
    return [...SCENARIOS, ...extra];
  }, [customTemplates]);

  // Day-by-day breakdown of the scenario currently being planned in the form —
  // lets the user pick a specific calendar date (e.g. "this Sunday") to override.
  const previewDays = useMemo(() => {
    if (!form.startDate) return [];
    const days = allScenarioDays[form.scenario] || [];
    return days.map((mdLabel, i) => ({ date: addDaysLocal(form.startDate, i), mdLabel }));
  }, [form.startDate, form.scenario, allScenarioDays]);

  // Baseline targets for one day of the form's scenario.
  // MD day: never scaled by training intensity — instead scaled by planned match
  // duration with +10% warmup allowance. All other days use the intensity slider.
  const getBaselineDayTargets = (mdLabel) => {
    const base = allTargets[form.scenario]?.[mdLabel];
    if (!base) return emptyDayTargets();
    let factor;
    if (mdLabel === 'MD') {
      const mdMins = form.mdMinutes ?? 90;
      factor = (mdMins / 90) * 1.1;
    } else {
      factor = (form.intensity ?? 100) / 100;
    }
    const out = {};
    POSITIONS.forEach(pos => {
      out[pos] = {};
      METRICS.forEach(m => {
        const cell = base[pos]?.[m] || { upper: 0, lower: 0 };
        out[pos][m] = { upper: Math.round(cell.upper * factor), lower: Math.round(cell.lower * factor) };
      });
    });
    return out;
  };

  const getDayTargetsForForm = (dateStr, mdLabel) =>
    form.targetOverrides?.[dateStr] || getBaselineDayTargets(mdLabel);

  const updateOverrideCell = (dateStr, mdLabel, pos, metric, bound, value) => {
    setForm(f => {
      const current = f.targetOverrides?.[dateStr] || getBaselineDayTargets(mdLabel);
      const updatedDay = {
        ...current,
        [pos]: { ...current[pos], [metric]: { ...current[pos]?.[metric], [bound]: value } },
      };
      return { ...f, targetOverrides: { ...f.targetOverrides, [dateStr]: updatedDay } };
    });
  };

  const resetOverrideDay = (dateStr) => {
    setForm(f => {
      const next = { ...f.targetOverrides };
      delete next[dateStr];
      return { ...f, targetOverrides: next };
    });
  };

  const toggleOverrideExpanded = (dateStr) => {
    setExpandedOverrideDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  };

  const loadTechnicalStats = () => getTechnicalStats().then(rows => setTechnicalStatsSessions(rows || [])).catch(err => console.error('Failed to load technical stats:', err));
  const loadCmjTests = () => getTests('cmj').then(rows => setCmjTests(rows || [])).catch(err => console.error('Failed to load CMJ tests:', err));
  const loadTrackingMatches = () => getTrackingMatches().then(rows => setTrackingMatches(rows || [])).catch(err => console.error('Failed to load tracking matches:', err));

  useEffect(() => {
    const load = async () => {
      try {
        const [p, sc, s, ct, opps, al, gk, rt] = await Promise.all([
          getPlayers(),
          getScenarios(),
          getGPS(),
          getLoadScenarios(),
          getOpponents(),
          getPlayerAliases(),
          getGkSessions(),
          getRawTargets(),
        ]);
        setCustomTemplates(ct || []);
        setBuiltInTables(rt || { targets: {}, scenarioDays: {} });
        setOpponents(opps || []);
        setAliases(al || []);
        setPlayers(p);
        setGkSessions(groupGkSessions(gk));
        loadTechnicalStats();
        loadCmjTests();
        loadTrackingMatches();
        setScenarios(sc.map(s => ({
          ...s,
          startDate: s.start_date ? s.start_date.slice(0, 10) : '',
          playerId: s.player_id,
        })));
        setSessions(s.map(sess => ({
          ...sess,
          date: sess.session_date ? sess.session_date.slice(0, 10) : '',
          title: sess.session_title,
          players: sess.players.map(p => ({
            ...p,
            playerId: p.player_id,
            mdLabel: p.md_label,
            metrics: {
              TD: p.td, RD: p.rd, HID: p.hid, SD: p.sd,
              SA: p.sa, Expl: p.expl, HMLD: p.hmld,
              maxSpeed: p.max_speed, avgHR: p.avg_hr,
              maxHR: p.max_hr, distPerMin: p.dist_per_min,
              sprints: p.sprints, hrZ1: p.hr_z1, hrZ2: p.hr_z2,
              hrZ3: p.hr_z3, hrZ4: p.hr_z4, hrZ5: p.hr_z5, hrZ6: p.hr_z6,
              entriesZone5: p.entries_zone5, avgMetabolicPower: p.avg_metabolic_power,
              hrv: p.hrv, modTrimp: p.mod_trimp,
            },
          })),
        })));
      } catch (err) {
        console.error('Failed to load GPS data:', err);
      }
    };
    load();
  }, []);

  const handleDeleteGkSession = async (session) => {
    if (!window.confirm(`Delete "${session.title || 'Session'}" (${session.date})? This removes ${session.players.length} player row(s).`)) return;
    try {
      await deleteGkSession(session.date, session.title);
      setGkSessions(prev => prev.filter(x => x.key !== session.key));
    } catch (err) {
      console.error('Failed to delete GK session:', err);
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Delete this GPS session? Only the GPS data (distances, drills, speeds) is removed — any technical stats, trainer ratings, or passing-drill data for the same day is kept. This cannot be undone.')) return;
    try {
      await deleteGPS(id);
      setSessions(prev => prev.filter(x => x.id !== id));
    } catch (err) {
      console.error('Failed to delete session:', err);
      alert('Failed to delete session. Please try again.');
    }
  };

  const handleDeleteTechnicalStats = async (session) => {
    if (!window.confirm(`Delete technical stats for "${session.session_title || session.opponent_name || 'Match'}" (${session.session_date?.slice(0, 10)})? This removes ${session.row_count} stat row(s) for ${session.player_count} player(s).`)) return;
    try {
      await deleteTechnicalStats(session.match_id);
      setTechnicalStatsSessions(prev => prev.filter(x => x.match_id !== session.match_id));
    } catch (err) {
      console.error('Failed to delete technical stats:', err);
    }
  };

  const handleDeleteCmjGroup = async (group) => {
    if (!window.confirm(`Delete CMJ tests for ${group.date}? This removes ${group.players.length} player result(s).`)) return;
    try {
      await Promise.all(group.players.map(r => deleteTest('cmj', r.player_id, group.date)));
      setCmjTests(prev => prev.filter(r => (r.test_date ? r.test_date.slice(0, 10) : '') !== group.date));
    } catch (err) {
      console.error('Failed to delete CMJ tests:', err);
    }
  };

  const handleDeleteTrackingMatch = async (match) => {
    if (!window.confirm(`Delete the KNVB tracking import for "${match.home_team} – ${match.away_team}" (${match.match_date?.slice(0, 10)})? This removes ${match.player_count} player row(s).`)) return;
    try {
      await deleteTrackingMatch(match.id);
      setTrackingMatches(prev => prev.filter(m => m.id !== match.id));
    } catch (err) {
      console.error('Failed to delete tracking match:', err);
    }
  };

  // ── Load scenario template handlers ────────────────────────────────────────
  const handleSaveTemplate = async (data) => {
    if (templateEditor && templateEditor !== 'new') {
      const updated = await updateLoadScenario(templateEditor.id, data);
      setCustomTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
    } else {
      const created = await createLoadScenario(data);
      setCustomTemplates(prev => [...prev, created]);
    }
    setTemplateEditor(null);
  };

  const handleDeleteTemplate = async (id) => {
    await deleteLoadScenario(id);
    setCustomTemplates(prev => prev.filter(t => t.id !== id));
  };

  const openAdd = () => { setForm(emptyScenario); setEditingId(null); setExpandedOverrideDates(new Set()); setShowEditPicker(false); setShowForm(true); };
  const openEdit = sc => { setForm({ scenario: sc.scenario, startDate: sc.startDate, playerId: sc.playerId, intensity: sc.intensity ?? 100, mdMinutes: sc.md_minutes ?? 90, targetOverrides: sc.target_overrides || {} }); setEditingId(sc.id); setExpandedOverrideDates(new Set()); setShowEditPicker(false); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setShowEditPicker(false); setEditingId(null); setForm(emptyScenario); setExpandedOverrideDates(new Set()); };

  // (scenario, start_date, player_id) is the period KEY every target consumer resolves against
  // — targetResolution.js's resolveScenarioPeriod picks it with a plain `.find()` over unordered
  // DB rows, so a SECOND row with the same key silently shadows the first, and whichever one the
  // user edits is likely the one nobody reads. That is exactly what happened on 2026-09-08: two
  // team rows for 3E/2026-09-06 existed, the MD-3 target overrides were saved into the shadowed
  // one, and Team Training kept showing the other row's unedited (110%-scaled) targets. Adding a
  // period that already exists therefore edits that row instead of creating a rival to it.
  const findDuplicatePeriod = () => scenarios.find(s =>
    s.scenario === form.scenario &&
    s.startDate === form.startDate &&
    String(s.playerId) === String(form.playerId)
  );

  const saveForm = async () => {
    if (!form.startDate) { alert('Please select a start date.'); return; }
    const duplicate = editingId ? null : findDuplicatePeriod();
    if (duplicate) {
      const who = form.playerId === 'team' ? 'the whole team' : players.find(p => String(p.id) === String(form.playerId))?.name || 'this player';
      if (!window.confirm(`A ${form.scenario} period starting ${form.startDate} already exists for ${who}.

Update that period instead of adding a second one? (Two periods with the same scenario and start date shadow each other — only one of them is ever used for targets.)`)) return;
    }
    const updateId = editingId || duplicate?.id || null;
    // day_intensity is deliberately NOT part of this payload: this form doesn't manage per-day
    // percentages (that's the Future Targets page), and PUT /api/scenarios treats an absent
    // day_intensity as "leave it alone". Sending `{}` from here is what used to wipe a period's
    // day-by-day % every time team targets were edited — don't add the field back.
    const payload = {
      scenario: form.scenario,
      start_date: form.startDate,
      player_id: form.playerId,
      intensity: form.intensity ?? 100,
      md_minutes: form.mdMinutes ?? 90,
      target_overrides: form.targetOverrides || {},
    };
    try {
      if (updateId) {
        const updated = await updateScenario(updateId, payload);
        setScenarios(prev => prev.map(s => s.id === updateId ? { ...updated, startDate: updated.start_date?.slice(0,10), playerId: updated.player_id, intensity: updated.intensity ?? form.intensity ?? 100, md_minutes: updated.md_minutes ?? form.mdMinutes ?? 90 } : s));
      } else {
        const created = await createScenario(payload);
        setScenarios(prev => [{ ...created, startDate: created.start_date?.slice(0,10), playerId: created.player_id }, ...prev]);
      }
      if (String(payload.player_id) === 'team') {
        const len = (allScenarioDays[payload.scenario] || []).length || 1;
        const isoWeekParam = dateStr => {
          const [y, m, d] = dateStr.split('-').map(Number);
          const t = new Date(Date.UTC(y, m - 1, d));
          t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
          const wk = Math.ceil(((t - Date.UTC(t.getUTCFullYear(), 0, 1)) / 86400000 + 1) / 7);
          return `${t.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
        };
        const weeks = [];
        for (let i = 0; i < len; i++) {
          const [y, m, d] = payload.start_date.split('-').map(Number);
          const day = new Date(Date.UTC(y, m - 1, d + i)).toISOString().slice(0, 10);
          const w = isoWeekParam(day);
          if (!weeks.includes(w)) weeks.push(w);
        }
        setPlannerOffer({ scenario: payload.scenario, weeks });
      }
      closeForm();
    } catch (err) {
      console.error('Failed to save scenario:', err);
      alert('Failed to save scenario.');
    }
  };

  const removeScenario = async id => {
    try {
      await deleteScenario(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete scenario:', err);
    }
  };

  const handleCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadStatus('processing');
    setUnmatchedMetricWarning(null);
    const reader = new FileReader();
    reader.onload = async evt => {
      const convertDate = str => {
        if (!str) return null;
        if (str.includes('/')) {
          const [d, m, y] = str.split('/');
          return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
        }
        return str;
      };
      try {
        // Papa.parse (2026-09-03 fix), not a hand-rolled split(',') — a naive split breaks the
        // moment any field contains a comma (e.g. a Drill Title like "Rondo, 4v2"), shifting
        // every column after it by one for that row. That's exactly what caused a real backfill
        // upload to 500 with "invalid input syntax for type date: 1040.44" — 1040.44 was a
        // Total Distance value that had slid into the Session Date column. Papa already handles
        // quoted/escaped fields correctly (same library handleCmjCSV below already uses for
        // ForceDecks CSVs), so this fixes it for every row, not just the one that happened to fail.
        const parsed = Papa.parse(evt.target.result, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.replace(/"/g, '').trim(),
          transform: v => (v === null || v === undefined ? '' : String(v).trim()),
        });
        const headers = parsed.meta.fields || [];
        const rows = parsed.data;
        const { headerMap: newMetricHeaderMap, unmatched: unmatchedNewMetrics } = resolveMetricHeaders(headers);
        if (unmatchedNewMetrics.length) setUnmatchedMetricWarning(unmatchedNewMetrics);

        const sessionMap = {};
        rows.forEach(row => {
          const firstName = (row['Player First Name'] || '').trim();
          const lastName  = (row['Player Last Name']  || '').trim();
          const fullName  = `${firstName} ${lastName}`.trim();
          if (!fullName) return;
          const date  = (row['Session Date'] || '').trim();
          const title = (row['Session Title'] || '').trim();
          const key   = `${date}__${title}`;
          if (!sessionMap[key]) sessionMap[key] = { date, title, playerMap: {} };
          const td = parseFloat(row['Total Distance']) || 0;
          // Two genuinely separate sessions sharing the same Session Date + Session Title
          // (e.g. two MD-2 sessions run the same day, confirmed 2026-07-30) land in this same
          // key/player bucket, each contributing its own "Entire Session" summary drill row.
          // Those are collected separately (entireSessionRows) and summed below to build that
          // player's load for the day, instead of the old highest-TD-wins pick that kept only
          // one session and silently discarded the other.
          const isEntireSession = (row['Drill Title'] || '').toLowerCase().includes('entire session');
          const existing = sessionMap[key].playerMap[fullName];
          if (!existing) {
            sessionMap[key].playerMap[fullName] = {
              row, td, drillRows: [row],
              entireSessionRows: isEntireSession ? [row] : [],
            };
          } else {
            existing.drillRows.push(row);
            if (isEntireSession) {
              existing.entireSessionRows.push(row);
            } else if (!existing.entireSessionRows.length && td > existing.td) {
              // No "Entire Session" row seen for this player yet — fall back to the old
              // highest-TD-wins pick (covers CSV shapes without a summary row).
              existing.row = row; existing.td = td;
            }
          }
        });

        // Individualized target resolution — batched through /api/targets/resolve-batch
        // (targetResolution.js, backend), scenarioName omitted so the server discovers the
        // active team scenario from the date itself (this preview doesn't know it yet). Fixes
        // the same non-deterministic team/individual precedence bug GPSData.js had, and — since
        // `scenario`/`mdLabel` get stored into playerEntries below, not just displayed — fixes it
        // for PERSISTED session data too, not just this preview. Keyed by the original CSV row
        // object (a stable reference reused unchanged into the second pass below), not by index,
        // so no fragile order-correlation between the two passes is needed.
        const resolvedByRow = new Map();
        {
          const rows = [];
          const items = [];
          Object.values(sessionMap).forEach(sess => {
            Object.values(sess.playerMap).forEach(({ row }) => {
              const fn = (row['Player First Name'] || '').trim();
              const ln = (row['Player Last Name']  || '').trim();
              const player = resolvePlayer(`${fn} ${ln}`.trim());
              rows.push(row);
              items.push({ key: String(rows.length - 1), playerId: player?.id || null, position: player?.position || null, date: convertDate(sess.date) });
            });
          });
          if (items.length) {
            try {
              const { results } = await resolveTargetsBatch(items);
              results.forEach((r, i) => resolvedByRow.set(rows[i], r));
            } catch (err) {
              console.error('Failed to resolve targets for CSV preview:', err);
            }
          }
        }

        const newSessions = Object.values(sessionMap).map(sess => {
          const isMatch = Object.values(sess.playerMap).some(pe =>
            (pe.drillRows || []).some(r => {
              const dt = (r['Drill Title'] || '').trim().toLowerCase();
              // Optional numeric suffix so non-standard formats split into more than 2
              // segments (e.g. "Match-First Half-Full-1"/"-2" for a 4x30 match) are still
              // recognized as a match, not just the standard 2x45 "-Full" title.
              return /^match-(first|second) half-full(-\d+)?$/.test(dt);
            })
          );
          const sessionType = isMatch ? 'match' : 'training';
          const opponent = opponents.find(o => o.name.toLowerCase() === sess.title.toLowerCase());
          const opponentId = opponent?.id || null;

          const playerEntries = Object.values(sess.playerMap).map(({ row, drillRows, entireSessionRows }) => {
            const fn = (row['Player First Name'] || '').trim();
            const ln = (row['Player Last Name']  || '').trim();
            const fullName = `${fn} ${ln}`.trim();
            const player = resolvePlayer(fullName);
            const pos = player?.position || null;
            const resolved = resolvedByRow.get(row);
            // Use the canonical player name when resolved (direct or via alias) so
            // an alias like "Tristan van Gils" never reaches stored data/reports —
            // only the true players.name ("Tristan van Gilst") does.
            const displayName = player?.name || fullName;
            return {
              name: displayName, playerId: player?.id || null, position: pos,
              scenario: resolved?.scenario || null, mdLabel: resolved?.dayLabel || null, targets: resolved?.targets || null,
              metrics: buildMetricsFromRows((entireSessionRows && entireSessionRows.length) ? entireSessionRows : [row], newMetricHeaderMap),
              drillEntries: (drillRows || []).map(dr => ({
                playerName: displayName,
                drillTitle: (dr['Drill Title'] || '').trim(),
                td:        parseFloat(dr['Total Distance']) || 0,
                rd:        parseFloat(dr['Distance Zone 4 - Zone 6 (Absolute)']) || 0,
                hid:       parseFloat(dr['High Speed Running (Absolute)']) || 0,
                sd:        parseFloat(dr['Distance Zone 6 (Absolute)']) || 0,
                sa:        (parseInt(dr['Accelerations (Absolute)']) || 0) + (parseInt(dr['Decelerations (Absolute)']) || 0),
                expl:      parseFloat(dr['Explosive Distance (Absolute)']) || 0,
                hmld:      parseFloat(dr['HML Distance']) || 0,
                maxSpeed:  parseFloat(dr['Max Speed']) || 0,
                totalTime: (dr['Total Time'] || '').trim(),
                avgHR:     Math.round(parseFloat(dr['Average Heart Rate']) || 0) || null,
                maxHR:     Math.round(parseFloat(dr['Max Heart Rate']) || 0) || null,
                // Metrics Table page (2026-09-03) — same fields as buildMetricsFromRows above,
                // but per-drill rather than summed/averaged across the whole session.
                entriesZone5:      parseFloat(dr['Entries Zone 5 (Relative)']) || 0,
                entriesZone6:      parseFloat(dr['Entries Zone 6 (Absolute)']) || 0,
                avgMetabolicPower: newMetricHeaderMap.avgMetabolicPower ? (parseFloat(dr[newMetricHeaderMap.avgMetabolicPower]) || 0) : 0,
                hrv:               newMetricHeaderMap.hrv ? (parseFloat(dr[newMetricHeaderMap.hrv]) || 0) : 0,
                modTrimp:          newMetricHeaderMap.modTrimp ? (parseFloat(dr[newMetricHeaderMap.modTrimp]) || 0) : 0,
                distPerMin:        parseFloat(dr['Distance Per Min']) || 0,
                hrZ1: dr['Time In Heart Rate Zone 1 (Relative)'] || '',
                hrZ2: dr['Time In Heart Rate Zone 2 (Relative)'] || '',
                hrZ3: dr['Time In Heart Rate Zone 3 (Relative)'] || '',
                hrZ4: dr['Time In Heart Rate Zone 4 (Relative)'] || '',
                hrZ5: dr['Time In Heart Rate Zone 5 (Relative)'] || '',
                hrZ6: dr['Time In Heart Rate Zone 6 (Relative)'] || '',
              })),
            };
          });
          return { date: sess.date, title: sess.title, sessionType, opponentId, players: playerEntries, id: Date.now() + Math.random() };
        });

        try {
          // A session already existing for this date+title is either a genuine correction
          // re-upload (replace — the long-standing default, e.g. fixing a mistake in the same
          // file) or a second real session sharing the same label (2026-07-30 — confirmed case:
          // two separate MD-2 sessions run on one day) that needs adding on top instead. The
          // client can't tell those apart automatically, so when it detects the conflict it
          // asks — silently guessing either way risks either losing a correction or double-
          // counting load.
          const hasConflict = newSessions.some(sess =>
            sessions.some(s => s.date === convertDate(sess.date) && s.title === sess.title)
          );
          const sumExisting = hasConflict && window.confirm(
            'A session already exists for this date/title.\n\n' +
            'Click OK to ADD this upload\'s load on top of the existing data (use this when two ' +
            'separate sessions share the same label, e.g. two MD-2 sessions on one day).\n\n' +
            'Click Cancel to REPLACE the existing data instead (the normal re-upload/correction behavior).'
          );

          for (const sess of newSessions) {
            const result = await saveGPS({
              date: convertDate(sess.date),
              title: sess.title,
              scenario: sess.players[0]?.scenario || null,
              sessionType: sess.sessionType,
              opponentId: sess.opponentId,
              skip_notification: !sendNotifications,
              sum_existing: sumExisting,
              players: sess.players.map(p => ({
                playerId: p.playerId,
                name: p.name,
                position: p.position,
                mdLabel: p.mdLabel,
                td:         p.metrics.TD,
                rd:         p.metrics.RD,
                hid:        p.metrics.HID,
                sd:         p.metrics.SD,
                sa:         p.metrics.SA,
                expl:       p.metrics.Expl,
                hmld:       p.metrics.HMLD,
                max_speed:  p.metrics.maxSpeed,
                player_max_speed: p.metrics.playerMaxSpeed,
                minutes_played: p.metrics.minutesPlayed,
                avg_hr:     p.metrics.avgHR,
                max_hr:     p.metrics.maxHR,
                dist_per_min: p.metrics.distPerMin,
                sprints:    p.metrics.sprints,
                hr_z1:      p.metrics.hrZ1,
                hr_z2:      p.metrics.hrZ2,
                hr_z3:      p.metrics.hrZ3,
                hr_z4:      p.metrics.hrZ4,
                hr_z5:      p.metrics.hrZ5,
                hr_z6:      p.metrics.hrZ6,
                entries_zone5: p.metrics.entriesZone5,
                avg_metabolic_power: p.metrics.avgMetabolicPower,
                hrv:        p.metrics.hrv,
                mod_trimp:  p.metrics.modTrimp,
              })),
            });
            const sessionId = result?.sessionId;
            if (sessionId) {
              const allDrills = sess.players.flatMap(p =>
                (p.drillEntries || []).map(d => ({
                  session_id: sessionId,
                  player_id:  p.playerId,
                  player_name: d.playerName,
                  drill_title: d.drillTitle,
                  td: d.td, rd: d.rd, hid: d.hid, sd: d.sd,
                  sa: d.sa, expl: d.expl, hmld: d.hmld,
                  max_speed: d.maxSpeed, total_time: d.totalTime,
                  avg_hr: d.avgHR, max_hr: d.maxHR,
                  entries_zone5: d.entriesZone5, entries_zone6: d.entriesZone6,
                  avg_metabolic_power: d.avgMetabolicPower, hrv: d.hrv, mod_trimp: d.modTrimp,
                  dist_per_min: d.distPerMin,
                  hr_z1: d.hrZ1, hr_z2: d.hrZ2, hr_z3: d.hrZ3, hr_z4: d.hrZ4, hr_z5: d.hrZ5, hr_z6: d.hrZ6,
                }))
              );
              if (allDrills.length > 0) {
                try { await saveDrillBatch(allDrills, { sumExisting }); } catch (e) {
                  console.warn('Drill save failed:', e.message);
                  alert(`Drill-level data failed to save for "${sess.title}" (${sess.date}): ${e.message}\n\nThe session's overall totals were still saved.`);
                }
              }
            }
          }
          const updated = await getGPS();
          setSessions(updated.map(sess => ({
            ...sess,
            date: sess.session_date?.slice(0, 10),
            title: sess.session_title,
            players: sess.players.map(p => ({
              ...p,
              playerId: p.player_id,
              mdLabel: p.md_label,
              metrics: {
                TD: p.td, RD: p.rd, HID: p.hid, SD: p.sd,
                SA: p.sa, Expl: p.expl, HMLD: p.hmld,
                maxSpeed: p.max_speed, avgHR: p.avg_hr,
                maxHR: p.max_hr, distPerMin: p.dist_per_min,
                sprints: p.sprints, hrZ1: p.hr_z1, hrZ2: p.hr_z2,
                hrZ3: p.hr_z3, hrZ4: p.hr_z4, hrZ5: p.hr_z5, hrZ6: p.hr_z6,
                entriesZone5: p.entries_zone5, avgMetabolicPower: p.avg_metabolic_power,
                hrv: p.hrv, modTrimp: p.mod_trimp,
              },
            })),
          })));
        } catch (err) {
          console.error('Failed to save GPS session:', err);
          alert(`Upload failed: ${err?.message || 'unknown error'}\n\nAny sessions processed before this point in the file were saved; anything after was not.`);
        }

        setUploadStatus('success');
        setTimeout(() => setUploadStatus(null), 3000);
        e.target.value = '';
      } catch(err) {
        console.log('Error:', err.message);
        setUploadStatus('error');
      }
    };
    reader.onerror = () => setUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // Maps the GK-specific CSV export (separate from the Statsports team export
  // above) into gk_sessions rows. Only "Entire Session" rows are kept — the
  // file also contains per-drill breakdown rows whose metrics are already
  // folded into that summary row.
  const GK_COLUMN_MAP = {
    'Player Name': 'player_name',
    'Session Date': 'session_date',
    'Drill Title': 'drill_title',
    'Session Title': 'session_title',
    'Dives': 'dives',
    'Dives Left': 'dives_left',
    'Dives Right': 'dives_right',
    'Average GK Power': 'avg_gk_power',
    'GK Load': 'gk_load',
    'Average Time Since Last Dive': 'avg_time_since_last_dive',
    'Total Distance': 'td',
    'Distance Zone 4 - Zone 6 (Absolute)': 'rd',
    'High Speed Running (Absolute)': 'hid',
    'Accelerations (Absolute)': 'acc',
    'Decelerations (Absolute)': 'dec',
  };

  const handleGkCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setGkUploadStatus('processing');
    setGkUploadResult(null);
    const convertDate = str => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
      }
      return str;
    };
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        // Papa.parse, not split(',') — same fix as the main GPS handler above. When a session has
        // no title in Statsports the export fills Session Title with the date ("October 7, 2026"),
        // and a naive split shifts every later column by one: 'Player Name' then reads the
        // Player Last Name value, so no player matches (and acc/dec/rd/hid read the wrong columns).
        const rawRows = Papa.parse(evt.target.result, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.replace(/"/g, '').trim(),
          transform: v => (v === null || v === undefined ? '' : String(v).trim()),
        }).data;
        const mapped = rawRows
          .map(raw => {
            const row = {};
            Object.entries(GK_COLUMN_MAP).forEach(([csvCol, field]) => { row[field] = raw[csvCol]; });
            row.session_date = convertDate(row.session_date);
            return row;
          })
          .filter(row => (row.drill_title || '').toLowerCase().includes('entire session'));
        const result = await uploadGkSessions(mapped);
        setGkUploadStatus('success');
        setGkUploadResult(result);
        const updatedGk = await getGkSessions();
        setGkSessions(groupGkSessions(updatedGk));
        e.target.value = '';
      } catch (err) {
        console.log('GK upload error:', err.message);
        setGkUploadStatus('error');
      }
    };
    reader.onerror = () => setGkUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // KNVB tracking (fysiek) CSV -> tracking_matches/tracking_player_totals (Team Comparison
  // feature, 2026-09-11). Unlike the naive split(',') the KNVB Technical Stats card below uses,
  // this one goes through PapaParse (already a dependency — see the main GPS CSV handler above)
  // with delimiter auto-detection, since the KNVB data portal's fysiek export can come out either
  // comma- or semicolon-delimited (semicolon exports use a decimal comma, which trackingImport.js
  // handles server-side). header:true also strips a leading BOM for us.
  const TRACKING_REQUIRED_COLUMNS = [
    'timeframe_label', 'position', 'source_team_name', 'source_home_team_name',
    'source_away_team_name', 'match_date', 'score_total_home', 'score_total_away',
    'source_match_id', 'source_player_id', 'player_name', 'tijd_in_seconden',
    'afstand_totaal', 'afstand_tussen_20_en_25_kmh', 'afstand_boven_25_kmh',
    'aantal_sprints_tussen_20_en_25_kmh', 'aantal_sprints_boven_25_kmh',
  ];
  // Period Comparison (2026-09-28): the 5-minute period rows ('0-5', '45+0-45+5', ...) are sent
  // too, trimmed to the columns tracking_period_totals stores — a full row is ~90 columns and a
  // match has ~440 period rows. Must match PERIOD_LABEL_RE / PERIOD_COLUMNS in trackingImport.js
  // (backend module, can't be imported from src/).
  const TRACKING_PERIOD_RE = /^\d+(?:\+\d+)?-\d+(?:\+\d+)?$/;
  const TRACKING_PERIOD_COLUMNS = [
    'timeframe_label', 'source_match_id', 'source_team_name', 'source_player_id', 'player_name',
    'position', 'tijd_in_seconden', 'afstand_totaal', 'afstand_tussen_20_en_25_kmh',
    'afstand_boven_25_kmh', 'aantal_sprints_tussen_20_en_25_kmh', 'aantal_sprints_boven_25_kmh',
    'aantal_acceleraties_boven_24', 'aantal_deceleraties_boven_24',
  ];
  const runTrackingUpload = async (rows, filename, overwrite) => {
    setTrackingUploadStatus('processing');
    setTrackingUploadError('');
    setTrackingExisting(null);
    try {
      const result = await uploadTrackingImport(rows, filename, overwrite);
      setTrackingUploadStatus('success');
      setTrackingUploadResult(result);
      setTrackingPendingUpload(null);
      loadTrackingMatches();
    } catch (err) {
      if (err.status === 409 && err.existing) {
        setTrackingUploadStatus('conflict');
        setTrackingExisting(err.existing);
        setTrackingPendingUpload({ rows, filename });
      } else {
        setTrackingUploadStatus('error');
        setTrackingUploadError(err.message || 'Error reading file.');
      }
    }
  };
  const handleTrackingCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setTrackingUploadResult(null);
    setTrackingPendingUpload(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: '', // auto-detect , vs ;
      complete: async results => {
        const totalRows = (results.data || []).filter(r => String(r.timeframe_label || '').trim().toLowerCase() === 'total');
        if (!totalRows.length) {
          setTrackingUploadStatus('error');
          setTrackingUploadError('No "total" rows found in this file — expected one per player.');
          e.target.value = '';
          return;
        }
        const present = new Set(Object.keys(totalRows[0]));
        const missing = TRACKING_REQUIRED_COLUMNS.filter(c => !present.has(c));
        if (missing.length) {
          setTrackingUploadStatus('error');
          setTrackingUploadError(`This file is missing: ${missing.join(', ')}. Export the physical (fysiek) match report from the KNVB data portal.`);
          e.target.value = '';
          return;
        }
        const periodRows = (results.data || [])
          .filter(r => TRACKING_PERIOD_RE.test(String(r.timeframe_label || '').trim()))
          .map(r => Object.fromEntries(TRACKING_PERIOD_COLUMNS.map(c => [c, r[c]])));
        await runTrackingUpload([...totalRows, ...periodRows], file.name, false);
        e.target.value = '';
      },
      error: () => { setTrackingUploadStatus('error'); setTrackingUploadError('Error reading file.'); },
    });
  };
  const handleTrackingOverwrite = () => {
    if (!trackingPendingUpload) return;
    runTrackingUpload(trackingPendingUpload.rows, trackingPendingUpload.filename, true);
  };

  // KNVB per-match player-stats CSV -> technical_match_stats (2026-08-17) — the sole Technical
  // Stats upload path now (SciSports's own handler/card were removed here when the club fully
  // switched sources; the backend importer is kept for historical backfill only, see
  // technicalImport.js). Same "no column remapping, send the raw parsed rows through as-is"
  // approach the old SciSports handler used — the backend's ingestKnvbCsv reads KNVB's own
  // column names (player_name, match_date, tackle/tackle_suc, etc.) directly.
  const handleTechKnvbCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setTechKnvbUploadStatus('processing');
    setTechKnvbUploadResult(null);
    setTechKnvbUploadError('');
    setResolvedAliasNamesKnvb(new Set());
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const lines = evt.target.result.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
        const rows = lines.slice(1).map(line => {
          const vals = line.split(',').map(v => v.replace(/"/g, '').trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
          return obj;
        });
        const result = await uploadTechnicalStatsKnvb(rows);
        setTechKnvbUploadStatus('success');
        setTechKnvbUploadResult(result);
        loadTechnicalStats();
        e.target.value = '';
      } catch (err) {
        setTechKnvbUploadStatus('error');
        setTechKnvbUploadError(err.message || 'Error reading file.');
      }
    };
    reader.onerror = () => setTechKnvbUploadStatus('error');
    reader.readAsText(file, 'UTF-8');
  };

  // ForceDecks (CMJ) CSV -> test_cmj, the same table/columns the manual Physical Test Input
  // CMJ form writes to (see server.production.js's /api/cmj/upload). A ForceDecks export mixes
  // every test type the device ran that session in one file and pads several header names with
  // a trailing space (e.g. "Jump Height (Imp-Mom) [cm] ") — transformHeader trims that before
  // any lookup, and only rows where Test Type is "CMJ" (case-insensitive) are kept, everything
  // else in the file is ignored.
  const handleCmjCSV = e => {
    const file = e.target.files[0];
    if (!file) return;
    setCmjUploadStatus('processing');
    setCmjUploadResult(null);
    setResolvedCmjAliasNames(new Set());
    const convertDate = str => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      return str;
    };
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim(),
      complete: async results => {
        try {
          const rows = (results.data || [])
            .filter(row => (row['Test Type'] || '').trim().toLowerCase() === 'cmj')
            .map(row => ({
              name: (row['Name'] || '').trim(),
              weight: parseFloat(row['BW [KG]']) || null,
              height_cm: parseFloat(row['Jump Height (Imp-Mom) [cm]']) || null,
              peak_power_wkg: parseFloat(row['Peak Power / BM [W/kg]']) || null,
              test_date: convertDate(row['Date']),
            }))
            .filter(row => row.name);
          const result = await uploadCmjTests(rows);
          setCmjUploadStatus('success');
          setCmjUploadResult(result);
          loadCmjTests();
          e.target.value = '';
        } catch (err) {
          console.error('CMJ upload error:', err.message);
          setCmjUploadStatus('error');
        }
      },
      error: () => setCmjUploadStatus('error'),
    });
  };

  const activePlayers = players.filter(p => p.active);
  const cmjGroups = useMemo(() => groupCmjTests(cmjTests), [cmjTests]);

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>GPS & Load — Upload</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Configure your scenario and import Statsports CSV files.</p>
      </div>

      {/* Scenario config */}
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Settings size={18} color="#3b82f6" />
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Active Scenarios</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowScenarioMap(true)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><MapIcon size={14} /> View</button>
            <button
              onClick={() => { setShowTemplatePicker(p => !p); setShowForm(false); setShowEditPicker(false); }}
              style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${showTemplatePicker ? '#8b5cf6' : 'var(--border-strong)'}`, background: showTemplatePicker ? 'rgba(139,92,246,0.1)' : 'var(--bg-app)', color: showTemplatePicker ? '#a78bfa' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
            ><BookOpen size={14} /> Templates</button>
            {scenarios.length > 0 && (
              <button
                onClick={() => { setShowForm(false); setShowEditPicker(p => !p); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${showEditPicker ? '#3b82f6' : 'var(--border-strong)'}`, background: showEditPicker ? 'rgba(59,130,246,0.1)' : 'var(--bg-app)', color: showEditPicker ? '#3b82f6' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Pencil size={14} /> Edit existing
              </button>
            )}
            <button onClick={openAdd} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ Add Scenario</button>
          </div>
        </div>

        {plannerOffer && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', marginBottom: 16, borderRadius: 10, border: '1px solid #1f6f43', background: 'rgba(31,111,67,0.08)', fontSize: 13 }}>
            <span style={{ flex: 1, minWidth: 200 }}>Scenario {plannerOffer.scenario} saved. Fill the Planner's schedule from it:</span>
            {plannerOffer.weeks.map(w => (
              <a key={w} href={`/app?page=planner&view=week&week=${w}&fill=1`} style={{ padding: '6px 12px', borderRadius: 8, background: '#1f6f43', color: '#fff', fontWeight: 600, textDecoration: 'none' }}>
                Week {Number(w.slice(6))}
              </a>
            ))}
            <button onClick={() => setPlannerOffer(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
          </div>
        )}

        {/* Template picker */}
        {showTemplatePicker && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Scenario Templates</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setTemplateEditor('new'); setShowTemplatePicker(false); }}
                  style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >+ New Template</button>
                <button onClick={() => setShowTemplatePicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
              </div>
            </div>
            {customTemplates.length === 0 ? (
              <p style={{ color: 'var(--text-dimmed)', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>No templates yet. Create your first one.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {customTemplates.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: '#a78bfa', fontSize: 15 }}>{t.name}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.days?.length ?? 0} days · {t.days?.join(', ')}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => { setTemplateEditor(t); setShowTemplatePicker(false); }}
                        style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
                      ><Pencil size={12} /> Edit</button>
                      <button
                        onClick={() => handleDeleteTemplate(t.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                      ><X size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit existing scenario picker */}
        {showEditPicker && !showForm && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', margin: 0 }}>Select a scenario to edit</h3>
              <button onClick={() => setShowEditPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {scenarios.map(sc => {
                const playerName = sc.playerId === 'team' ? 'Whole team' : players.find(p => String(p.id) === String(sc.playerId))?.name || 'Unknown';
                return (
                  <button
                    key={sc.id}
                    onClick={() => openEdit(sc)}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                  >
                    <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 16, minWidth: 40 }}>{sc.scenario}</span>
                    {(sc.intensity ?? 100) !== 100 && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', padding: '2px 7px', borderRadius: 5 }}>{sc.intensity}%</span>
                    )}
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Started {sc.startDate}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>→ {playerName}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Pencil size={12} /> Edit
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: 20, marginBottom: 16, border: '1px solid var(--border-strong)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>{editingId ? 'Edit Scenario' : 'New Scenario'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Scenario</label>
                <select value={form.scenario} onChange={e => setForm(f => ({ ...f, scenario: e.target.value }))} style={inputStyle}>
                  {allScenarioNames.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Applies to</label>
                <select value={form.playerId} onChange={e => setForm(f => ({ ...f, playerId: e.target.value }))} style={inputStyle}>
                  <option value="team">Whole team</option>
                  {activePlayers.sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Start date (= day 1 of scenario)</label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>Load intensity</span>
                <span style={{ color: form.intensity === 100 ? 'var(--text-muted)' : '#f59e0b', fontWeight: 600 }}>{form.intensity ?? 100}%</span>
              </label>
              <input
                type="range" min={50} max={120} step={5}
                value={form.intensity ?? 100}
                onChange={e => setForm(f => ({ ...f, intensity: parseInt(e.target.value) }))}
                style={{ width: '100%', accentColor: '#3b82f6', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
                <span>50%</span><span>75%</span><span>100%</span><span>120%</span>
              </div>
            </div>

            {previewDays.some(d => d.mdLabel === 'MD') && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                  Match duration <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>(affects MD targets only — scales + 10% warmup)</span>
                </label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {[45, 60, 75, 90].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, mdMinutes: m }))}
                      style={{
                        padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${(form.mdMinutes ?? 90) === m ? '#3b82f6' : 'var(--border-strong)'}`,
                        background: (form.mdMinutes ?? 90) === m ? 'rgba(59,130,246,0.15)' : 'transparent',
                        color: (form.mdMinutes ?? 90) === m ? '#60a5fa' : 'var(--text-secondary)',
                      }}
                    >
                      {m} min
                    </button>
                  ))}
                  <input
                    type="number" min={1} max={120}
                    value={form.mdMinutes ?? 90}
                    onChange={e => setForm(f => ({ ...f, mdMinutes: parseInt(e.target.value) || 90 }))}
                    style={{ width: 60, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>min</span>
                </div>
              </div>
            )}

            {previewDays.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                  Day-by-day targets <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>(optional overrides for specific dates)</span>
                </label>
                <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
                  {previewDays.map(({ date, mdLabel }) => {
                    const hasOverride = !!form.targetOverrides?.[date];
                    const expanded = expandedOverrideDates.has(date);
                    const dateLabel = new Date(date).toLocaleDateString('nl-NL', { weekday: 'short', day: '2-digit', month: 'short' });
                    return (
                      <div key={date} style={{ borderTop: '1px solid var(--border)' }}>
                        <div
                          onClick={() => toggleOverrideExpanded(date)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: hasOverride ? 'rgba(245,158,11,0.06)' : 'transparent' }}
                        >
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', minWidth: 90 }}>{dateLabel}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 50 }}>{mdLabel}</span>
                          {hasOverride && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', borderRadius: 99, padding: '2px 8px' }}>Overridden</span>
                          )}
                          <span style={{ flex: 1 }} />
                          <span style={{ fontSize: 11, color: '#60a5fa' }}>{expanded ? 'Hide' : 'Edit targets'}</span>
                        </div>
                        {expanded && (
                          <div style={{ padding: '0 12px 12px' }}>
                            <DayMetricsTable
                              targets={getDayTargetsForForm(date, mdLabel)}
                              onChange={(pos, metric, bound, val) => updateOverrideCell(date, mdLabel, pos, metric, bound, val)}
                            />
                            {hasOverride && (
                              <button
                                type="button"
                                onClick={() => resetOverrideDay(date)}
                                style={{ marginTop: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}
                              >
                                Reset to scenario default
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={closeForm} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveForm} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{editingId ? 'Update Scenario' : 'Save Scenario'}</button>
            </div>
          </div>
        )}

        {scenarios.length === 0 && !showForm && (
          <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>No scenarios configured yet.</p>
        )}
        {(showAllScenarios ? scenarios : scenarios.slice(0, 5)).map(sc => {
          const playerName = sc.playerId === 'team' ? 'Whole team' : players.find(p => String(p.id) === String(sc.playerId))?.name || 'Unknown';
          return (
            <div key={sc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: 16 }}>{sc.scenario}</span>
                {(sc.intensity ?? 100) !== 100 && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', background: '#451a03', padding: '2px 7px', borderRadius: 5 }}>{sc.intensity}%</span>
                )}
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Started {sc.startDate} · Day 1 = {allScenarioDays[sc.scenario]?.[0]}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>→ {playerName}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => openEdit(sc)} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => removeScenario(sc.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
        {scenarios.length > 5 && (
          <button
            onClick={() => setShowAllScenarios(s => !s)}
            style={{ width: '100%', padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border-strong)', background: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {showAllScenarios ? 'Show less' : `Show all (${scenarios.length - 5} more)`}
          </button>
        )}
      </div>

      {/* CSV Upload */}
      <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Upload size={18} color="#10b981" />
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import Statsports CSV</h2>
          </div>
          <button onClick={() => setSendNotifications(v => !v)} title="Toggle push notifications for this upload"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: sendNotifications ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: 0 }}>
            {sendNotifications ? <ToggleRight size={26} /> : <ToggleLeft size={26} />}
            {sendNotifications ? 'Notifications on' : <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><BellOff size={13} /> Notifications off</span>}
          </button>
        </div>
        {!sendNotifications && (
          <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: '#451a03', color: '#fbbf24', fontSize: 12.5 }}>
            Players and staff won't be notified for sessions uploaded while this is off — useful when backfilling old matches.
          </div>
        )}
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('gps-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your Statsports CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Supports single or multi-session exports</p>
          <input id="gps-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCSV} />
        </div>
        {uploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {uploadStatus === 'success'    && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><CheckCircle size={18} /> Session imported successfully!</div>}
        {uploadStatus === 'error'      && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}
        {unmatchedMetricWarning && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fbbf24', fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <AlertTriangle size={16} /> Some Metrics Table columns weren't found in this CSV
              </span>
              <button onClick={() => setUnmatchedMetricWarning(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 2, display: 'flex' }}><X size={14} /></button>
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {unmatchedMetricWarning.map(({ label, tried }) => (
                <li key={label}>{label} — tried column name{tried.length > 1 ? 's' : ''}: {tried.map(t => `"${t}"`).join(', ')}</li>
              ))}
            </ul>
            <div style={{ marginTop: 6 }}>These will show as blank on the Metrics Table page until the real column name is confirmed.</div>
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Shield size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import GK CSV</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('gk-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your GK session CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Only "Entire Session" rows are imported per player/session</p>
          <input id="gk-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleGkCSV} />
        </div>
        {gkUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {gkUploadStatus === 'success' && gkUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Imported {gkUploadResult.count} session row{gkUploadResult.count === 1 ? '' : 's'}.
            </div>
            {gkUploadResult.unmatched?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)', color: '#fca5a5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 4 }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported):
                </div>
                <div style={{ textAlign: 'center' }}>{gkUploadResult.unmatched.join(', ')}</div>
              </div>
            )}
          </div>
        )}
        {gkUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        {/* KNVB tracking (fysiek) — Team Comparison feature (2026-09-11). Opponent physical
            team totals, converted to HeraLabX-equivalent numbers on the comparison page itself;
            this card only imports and stores the raw per-player totals. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Users size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import KNVB Tracking CSV (fysiek)</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('tracking-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse a KNVB physical match report CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>One file per match — used for Team Comparison, opponent physical totals only</p>
          <input id="tracking-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleTrackingCSV} />
        </div>
        {trackingUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {trackingUploadStatus === 'success' && trackingUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}>
              <CheckCircle size={18} />
              {trackingUploadResult.overwritten ? 'Updated' : 'Imported'}: {trackingUploadResult.homeTeam} – {trackingUploadResult.awayTeam} · {trackingUploadResult.matchDate} · {trackingUploadResult.scoreHome}–{trackingUploadResult.scoreAway} · {trackingUploadResult.matchDurationMin.toFixed(1)} min · {trackingUploadResult.nHomeOutfield} v {trackingUploadResult.nAwayOutfield} outfield · HSR {trackingUploadResult.hsrShareHomePct.toFixed(1)}% / {trackingUploadResult.hsrShareAwayPct.toFixed(1)}%
            </div>
            <div style={{ marginTop: 8, textAlign: 'center', color: trackingUploadResult.heraclesMatchLinked ? '#6ee7b7' : '#fcd34d' }}>
              {trackingUploadResult.heraclesMatchLinked
                ? `Linked to GPS match #${trackingUploadResult.heraclesMatchId}.`
                : `No linked GPS match found${trackingUploadResult.linkNote ? ` — ${trackingUploadResult.linkNote}` : '.'}`}
            </div>
            <div style={{ marginTop: 8, textAlign: 'center', color: trackingUploadResult.periodCount ? '#6ee7b7' : '#fcd34d' }}>
              {trackingUploadResult.periodCount
                ? `${trackingUploadResult.periodCount} five-minute periods stored (${trackingUploadResult.periodRowCount} player rows).`
                : 'No five-minute period rows in this file — Advanced comparison will not be available for this match.'}
            </div>
            {trackingUploadResult.qualityFlag && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)', color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}>
                <AlertTriangle size={16} /> Quality flag: {trackingUploadResult.qualityFlag} — excluded from calibration ({trackingUploadResult.exclusionReason}).
              </div>
            )}
          </div>
        )}
        {trackingUploadStatus === 'conflict' && trackingExisting && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fcd34d', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}>
              <AlertTriangle size={18} /> Already imported: {trackingExisting.homeTeam} – {trackingExisting.awayTeam} · {String(trackingExisting.matchDate).slice(0, 10)} · {trackingExisting.scoreHome}–{trackingExisting.scoreAway} (uploaded {String(trackingExisting.uploadedAt).slice(0, 10)}{trackingExisting.sourceFilename ? `, ${trackingExisting.sourceFilename}` : ''}).
            </div>
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <button onClick={handleTrackingOverwrite} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Overwrite</button>
            </div>
          </div>
        )}
        {trackingUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}><AlertTriangle size={18} /> {trackingUploadError || 'Error reading file.'}</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        {/* SciSports's own upload card was removed here 2026-08-17 — the club fully switched to
            KNVB as the Technical Stats source. The backend importer (ingestScisportsCsv) and its
            endpoint are kept working for historical backfill, just not offered in this UI
            anymore; see technicalImport.js's header comment. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <BarChart3 size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import Technical Stats CSV (KNVB)</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('technical-knvb-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse a KNVB player-stats CSV</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>One file per match — Switch Play has no KNVB source and stays unscored for matches uploaded this way</p>
          <input id="technical-knvb-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleTechKnvbCSV} />
        </div>
        {techKnvbUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {techKnvbUploadStatus === 'success' && techKnvbUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Wrote {techKnvbUploadResult.rowsWritten} technical_match_stats row{techKnvbUploadResult.rowsWritten === 1 ? '' : 's'} for match #{techKnvbUploadResult.matchId}.
            </div>
            {techKnvbUploadResult.unmatchedPlayers?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#fca5a5' }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported) — opponent rows are expected here too:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {techKnvbUploadResult.unmatchedPlayers.map(name => (
                    resolvedAliasNamesKnvb.has(name) ? (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6ee7b7', padding: '6px 0' }}>
                        <CheckCircle size={13} /> {name} — aliased, re-upload this CSV to import their stats.
                      </div>
                    ) : (
                      <UnmatchedNameResolver
                        key={name}
                        name={name}
                        players={players}
                        onResolved={resolvedName => setResolvedAliasNamesKnvb(prev => new Set(prev).add(resolvedName))}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {techKnvbUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', textAlign: 'center' }}><AlertTriangle size={18} /> {techKnvbUploadError || 'Error reading file.'}</div>}

        <div style={{ borderTop: '1px solid var(--border-strong)', margin: '24px 0 16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Activity size={18} color="#3b82f6" />
          <h2 style={{ fontSize: 16, fontWeight: 700 }}>Import CMJ CSV (ForceDecks)</h2>
        </div>
        <div style={{ border: '2px dashed var(--border-strong)', borderRadius: 10, padding: 32, textAlign: 'center', cursor: 'pointer' }}
          onClick={() => document.getElementById('cmj-file-input').click()}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}>
          <Upload size={32} style={{ margin: '0 auto 12px', display: 'block', color: 'var(--text-muted)' }} />
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Click to browse your ForceDecks export</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Only "CMJ" test rows are imported — other test types in the file are ignored</p>
          <input id="cmj-file-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleCmjCSV} />
        </div>
        {cmjUploadStatus === 'processing' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#1e3a5f', color: '#93c5fd', fontSize: 14, textAlign: 'center' }}>Processing CSV...</div>}
        {cmjUploadStatus === 'success' && cmjUploadResult && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#064e3b', color: '#6ee7b7', fontSize: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <CheckCircle size={18} /> Imported {cmjUploadResult.count} CMJ result{cmjUploadResult.count === 1 ? '' : 's'}.
            </div>
            {cmjUploadResult.unmatchedPlayers?.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#fca5a5' }}>
                  <AlertTriangle size={16} /> Unmatched player names (not imported):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {cmjUploadResult.unmatchedPlayers.map(name => (
                    resolvedCmjAliasNames.has(name) ? (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6ee7b7', padding: '6px 0' }}>
                        <CheckCircle size={13} /> {name} — aliased, re-upload this CSV to import their result.
                      </div>
                    ) : (
                      <UnmatchedNameResolver
                        key={name}
                        name={name}
                        players={players}
                        onResolved={resolvedName => setResolvedCmjAliasNames(prev => new Set(prev).add(resolvedName))}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {cmjUploadStatus === 'error' && <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}><AlertTriangle size={18} /> Error reading file.</div>}
      </div>

      {/* Imported sessions — GPS, GK, Technical Stats, CMJ, Tracking Matches. Each tile shows
          only the latest 3 (CollapsibleList, 2026-09-11) — these lists only ever grow, and were
          pushing later cards far down the page. */}
      {(sessions.length > 0 || gkSessions.length > 0 || technicalStatsSessions.length > 0 || cmjGroups.length > 0 || trackingMatches.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 16 }}>
          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Calendar size={18} color="#8b5cf6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>GPS Sessions ({sessions.length})</h2>
            </div>
            {sessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No GPS sessions yet.</div>
            ) : (
              <CollapsibleList
                items={[...sessions].sort((a, b) => new Date(b.date) - new Date(a.date))}
                renderItem={s => (
                  <div key={s.id} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.title || 'Session'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.date}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.players.length} players</span>
                      <button onClick={() => handleDeleteSession(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Shield size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>GK Sessions ({gkSessions.length})</h2>
            </div>
            {gkSessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No GK sessions yet.</div>
            ) : (
              <CollapsibleList
                items={gkSessions}
                renderItem={s => (
                  <div key={s.key} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.title || 'Session'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.date}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.players.length} player{s.players.length === 1 ? '' : 's'}</span>
                      <button onClick={() => handleDeleteGkSession(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <BarChart3 size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Technical Stats ({technicalStatsSessions.length})</h2>
            </div>
            {technicalStatsSessions.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No technical stats yet.</div>
            ) : (
              <CollapsibleList
                items={technicalStatsSessions}
                renderItem={s => (
                  <div key={s.match_id} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{s.opponent_name || s.session_title || 'Match'}</div>
                      {/* Source badge(s) — 2026-08-17, alongside the KNVB importer. A match showing
                          both means a second upload may have partially overwritten the first for
                          any KPI both sources cover (see technicalImport.js's ingestKnvbCsv comment). */}
                      {(s.sources || []).map(src => (
                        <span key={src} style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, color: src === 'knvb' ? '#93c5fd' : '#fcd34d', background: src === 'knvb' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)' }}>
                          {src}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{s.session_date?.slice(0, 10)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.player_count} player{s.player_count === 1 ? '' : 's'} · {s.row_count} rows</span>
                      <button onClick={() => handleDeleteTechnicalStats(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Activity size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>CMJ Tests ({cmjGroups.length})</h2>
            </div>
            {cmjGroups.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No CMJ tests yet.</div>
            ) : (
              <CollapsibleList
                items={cmjGroups}
                renderItem={g => (
                  <div key={g.key} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>CMJ</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{g.date}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{g.players.length} player{g.players.length === 1 ? '' : 's'}</span>
                      <button onClick={() => handleDeleteCmjGroup(g)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Users size={18} color="#3b82f6" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Tracking Matches ({trackingMatches.length})</h2>
            </div>
            {trackingMatches.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>No tracking matches yet.</div>
            ) : (
              <CollapsibleList
                items={trackingMatches}
                renderItem={m => (
                  <div key={m.id} style={{ padding: '10px 14px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{m.home_team} – {m.away_team}</div>
                      {!m.heracles_match_id && (
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, color: '#fcd34d', background: 'rgba(245,158,11,0.15)' }}>no GPS link</span>
                      )}
                      {m.quality_flag && (
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, color: '#fca5a5', background: 'rgba(239,68,68,0.15)' }}>flagged</span>
                      )}
                      {!m.period_count && (
                        <span title="Imported before period rows were stored — re-upload the fysiek CSV (overwrite) to enable Advanced comparison." style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, color: '#fcd34d', background: 'rgba(245,158,11,0.15)' }}>no periods</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{m.match_date?.slice(0, 10)} · {m.score_home}–{m.score_away}</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.player_count} player{m.player_count === 1 ? '' : 's'}{m.period_count ? ` · ${m.period_count} periods` : ''}</span>
                      <button onClick={() => handleDeleteTrackingMatch(m)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={14} /></button>
                    </div>
                  </div>
                )}
              />
            )}
          </div>
        </div>
      )}

      <DocumentUploadSection players={players} />

      {/* Custom scenario templates list */}
      {customTemplates.length > 0 && (
        <div style={{ background: 'var(--border)', borderRadius: 12, padding: 24, border: '1px solid var(--border-strong)', marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <BookOpen size={18} color="#a78bfa" />
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Custom Scenario Templates</h2>
            </div>
            <button onClick={() => setTemplateEditor('new')} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ New Template</button>
          </div>
          {customTemplates.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border-strong)' }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: '#a78bfa', fontSize: 15 }}>{t.name}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t.days?.length ?? 0} days · {t.days?.join(', ')}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setTemplateEditor(t)} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><Pencil size={12} /> Edit</button>
                <button onClick={() => handleDeleteTemplate(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><X size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scenario template editor modal */}
      {templateEditor && (
        <ScenarioTemplateEditor
          editing={templateEditor === 'new' ? null : templateEditor}
          allBuiltInTargets={allTargets}
          allBuiltInDays={allScenarioDays}
          allBuiltInLabels={allLabels}
          allScenarioNames={allScenarioNames}
          onSave={handleSaveTemplate}
          onClose={() => setTemplateEditor(null)}
        />
      )}

      {/* Scenario map modal */}
      {showScenarioMap && (
        <ScenarioMapModal
          scenarioDays={allScenarioDays}
          customTemplates={customTemplates}
          onClose={() => setShowScenarioMap(false)}
        />
      )}
    </div>
  );
}